import { MoneyError, type Pence } from './money.js';
import { DRINKS, MEAL_SIDES, MEAL_SIDE_ITEM, isFlavour, isProtein, isSauce, isTopping, type Flavour, type Menu, type MenuItem, type Protein, type Sauce, type Topping } from './menu.js';

export interface Portion {
  flavour: Flavour;
  protein: Protein;
}

/** A reward applied to a bag line. Points are reserved while in the bag and spent on collection. */
export type LineReward =
  | { kind: 'points'; rewardId: 'side' | 'main'; points: number }
  | { kind: 'code'; code: string }
  | { kind: 'spin'; prize: 'dessert' }
  /** The referrer's free item (a 'referral_item' voucher, earned when a friend collects their first order). */
  | { kind: 'referral' };

/** What the customer configured. Prices are never stored here; the server works them out. */
export interface LineConfig {
  itemId: string;
  /** Index into `MenuItem.sizes`. For tikka items the server may bump this up (a third portion makes it Large). */
  sizeIndex: number;
  portions: Portion[];
  sauces: Sauce[];
  toppingsOff: Topping[];
  upgradeIds: string[];
  meal?: { side: string; drink?: string } | undefined;
  reward?: LineReward | null | undefined;
  /** Members' price dessert add-on (prototype: Gulab Jamun for £1). */
  membersPrice?: boolean | undefined;
  /** The till's sauce and salad changes on a main, as their labels ("NO ONION"). Free; house sauces stay on top otherwise. */
  changes?: string[] | undefined;
  /** Group order (docs/28): whose line this is. Goes on the kitchen ticket and the box label; never changes the price. */
  forName?: string | undefined;
}

export interface BagLine {
  id: string;
  config: LineConfig;
  qty: number;
}

export interface PricedLine {
  id: string;
  itemId: string;
  name: string;
  sizeName: string;
  sizeIndex: number;
  qty: number;
  unitPence: Pence;
  linePence: Pence;
  /** Points reserved by this line (reward lines only), already multiplied by qty. */
  pointsReserved: number;
  isMain: boolean;
  isReward: boolean;
}

export class PricingError extends MoneyError {
  override name = 'PricingError';
  constructor(message: string, public readonly code: string) {
    super(message);
  }
}

function getItem(menu: Menu, itemId: string): MenuItem {
  const it = menu.items[itemId];
  if (!it) throw new PricingError(`Unknown menu item "${itemId}"`, 'unknown_item');
  return it;
}

/**
 * Work out the size a tikka item should be given its portions.
 * Rule: Regular holds up to 2 portions; a third portion makes it Large.
 * A customer may choose a larger size than needed, never a smaller one.
 */
export function resolveSizeIndex(item: MenuItem, requestedIndex: number, portionCount: number): number {
  if (!Number.isInteger(requestedIndex) || requestedIndex < 0 || requestedIndex >= item.sizes.length) {
    throw new PricingError(`"${item.name}" has no size #${requestedIndex}`, 'bad_size');
  }
  if (!item.hasTikkas) return requestedIndex;
  let idx = requestedIndex;
  while (idx < item.sizes.length && (item.sizes[idx]?.maxPortions ?? Infinity) < portionCount) idx++;
  if (idx >= item.sizes.length) {
    throw new PricingError(`Too many portions for "${item.name}"`, 'too_many_portions');
  }
  return idx;
}

/** Validate a config against the menu. Throws PricingError with a stable `code`. */
export function validateConfig(menu: Menu, cfg: LineConfig): { item: MenuItem; sizeIndex: number } {
  const item = getItem(menu, cfg.itemId);
  if (item.soldOut) throw new PricingError(`"${item.name}" is sold out`, 'sold_out');
  for (const p of cfg.portions) {
    if (menu.soldOutFlavours.includes(p.flavour)) {
      throw new PricingError(`${p.flavour} is sold out today`, 'flavour_sold_out');
    }
    if ((menu.soldOutPortions ?? []).some((x) => x.flavour === p.flavour && x.protein === p.protein)) {
      throw new PricingError(`${p.flavour} ${p.protein} is sold out today`, 'flavour_sold_out');
    }
  }
  if (item.hasTikkas && cfg.portions.length === 0) {
    throw new PricingError(`Pick at least one flavour for "${item.name}"`, 'no_portions');
  }
  if (!item.hasTikkas && cfg.portions.length > 0) {
    throw new PricingError(`"${item.name}" doesn't take flavours`, 'portions_not_allowed');
  }
  for (const u of cfg.upgradeIds) {
    if (!item.upgradeIds.includes(u) || !menu.upgrades[u]) {
      throw new PricingError(`"${u}" isn't available on "${item.name}"`, 'bad_upgrade');
    }
  }
  if (cfg.meal && !item.mealAvailable) {
    throw new PricingError(`"${item.name}" can't be made a meal`, 'meal_not_allowed');
  }
  if (cfg.meal) {
    if (!(MEAL_SIDES as readonly string[]).includes(cfg.meal.side)) throw new PricingError(`"${cfg.meal.side}" isn't a meal side`, 'bad_meal');
    const sideItem = menu.items[MEAL_SIDE_ITEM[cfg.meal.side as keyof typeof MEAL_SIDE_ITEM]];
    if (sideItem?.soldOut) throw new PricingError(`${cfg.meal.side} is sold out today`, 'sold_out');
    if (cfg.meal.drink !== undefined && !(DRINKS as readonly string[]).includes(cfg.meal.drink)) throw new PricingError(`"${cfg.meal.drink}" isn't a drink we do`, 'bad_meal');
  }
  for (const p of cfg.portions) {
    if (!isFlavour(p.flavour) || !isProtein(p.protein)) throw new PricingError('Unknown flavour or protein', 'bad_portion');
  }
  for (const s of cfg.sauces) if (!isSauce(s)) throw new PricingError(`"${s}" isn't a house sauce`, 'bad_sauce');
  if (new Set(cfg.sauces).size !== cfg.sauces.length) throw new PricingError('Each sauce once', 'bad_sauce');
  for (const t of cfg.toppingsOff) if (!isTopping(t)) throw new PricingError(`"${t}" isn't a topping`, 'bad_topping');
  if (cfg.toppingsOff.length && !item.hasToppings) throw new PricingError(`"${item.name}" has no toppings to remove`, 'bad_topping');
  if (new Set(cfg.upgradeIds).size !== cfg.upgradeIds.length) throw new PricingError('Each upgrade once', 'bad_upgrade');
  if (cfg.changes !== undefined) {
    if (!Array.isArray(cfg.changes) || cfg.changes.length > 16 || cfg.changes.some((c) => typeof c !== 'string' || !c.trim() || c.length > 24)) throw new PricingError('Sauce and salad changes are short labels', 'bad_change');
    if (cfg.changes.length && !item.hasTikkas) throw new PricingError(`"${item.name}" has no sauce or salad to change`, 'bad_change');
  }
  const sizeIndex = resolveSizeIndex(item, cfg.sizeIndex, cfg.portions.length);
  const r = cfg.reward;
  if (r && r.kind === 'points') {
    if (r.rewardId === 'main' && !item.hasTikkas) throw new PricingError('A free main must be a bowl, wrap or Just Tikkas', 'reward_mismatch');
    if (r.rewardId === 'side' && !((item.category === 'sides' && sizeIndex === 0) || item.category === 'desserts')) {
      throw new PricingError('A free side must be a Regular side or a dessert', 'reward_mismatch');
    }
  }
  if (r && r.kind === 'spin' && item.category !== 'desserts') {
    throw new PricingError('The spin prize is a dessert', 'reward_mismatch');
  }
  if (cfg.membersPrice && item.category !== 'desserts') {
    throw new PricingError("Members' price only applies to the dessert add-on", 'members_price_mismatch');
  }
  return { item, sizeIndex };
}

/**
 * Price of one unit of a configured line, in pence.
 * - Free Regular main: £0. If they upsize to Large they pay the difference.
 * - Free side/dessert and spin prize: £0.
 * - Upgrades and "make it a meal" are always charged, even on reward lines.
 */
export function unitPrice(menu: Menu, cfg: LineConfig): Pence {
  const { item, sizeIndex } = validateConfig(menu, cfg);
  const size = item.sizes[sizeIndex]!;
  const regular = item.sizes[0]!;
  let p: Pence;
  const r = cfg.reward;
  if (r) {
    p = r.kind === 'points' && r.rewardId === 'main' && sizeIndex > 0 ? size.pricePence - regular.pricePence : 0;
  } else if (cfg.membersPrice) {
    p = menu.membersDessertPricePence;
  } else {
    p = size.pricePence;
  }
  for (const u of cfg.upgradeIds) p += menu.upgrades[u]!.pricePence;
  if (cfg.meal) p += menu.mealPricePence;
  return p;
}

export function priceLine(menu: Menu, line: BagLine): PricedLine {
  if (!Number.isInteger(line.qty) || line.qty < 1) throw new PricingError('Quantity must be at least 1', 'bad_qty');
  const { item, sizeIndex } = validateConfig(menu, line.config);
  const unitPence = unitPrice(menu, line.config);
  const r = line.config.reward;
  const pointsEach = r && r.kind === 'points' ? r.points : 0;
  return {
    id: line.id,
    itemId: item.id,
    name: item.name,
    sizeName: item.sizes[sizeIndex]!.name,
    sizeIndex,
    qty: line.qty,
    unitPence,
    linePence: unitPence * line.qty,
    pointsReserved: pointsEach * line.qty,
    isMain: item.hasTikkas,
    isReward: !!r,
  };
}

export function priceBag(menu: Menu, lines: readonly BagLine[]): { lines: PricedLine[]; subtotalPence: Pence; pointsReserved: number } {
  const priced = lines.map((l) => priceLine(menu, l));
  return {
    lines: priced,
    subtotalPence: priced.reduce((a, l) => a + l.linePence, 0),
    pointsReserved: priced.reduce((a, l) => a + l.pointsReserved, 0),
  };
}
